import { getCartIdentity } from './cartIdentity'
import config from 'src/config'
import { COMMERCE_TIMEZONE, normalizeCommerceSearchQuery, type CommerceRecommendationContext } from 'src/shared/commerceAnalytics'

type CommerceItem = { id: number; key?: string; qty: number }
type CommerceEvent = 'visit' | 'add_to_cart' | 'begin_checkout' | 'search' | 'recommendation_click' | 'recommendation_add'
type CommerceDetails = { items?: CommerceItem[]; query?: string; resultCount?: number; recommendation?: CommerceRecommendationContext }

// A card and its containing homepage may observe the same native click. Count
// that interaction once, without suppressing a later intentional second click.
const recommendationClicks = new WeakSet<Event>()
export const trackRecommendationClick = (
  event: Event,
  item: { id: number; key?: string },
  recommendation: CommerceRecommendationContext | undefined,
): void => {
  if (!recommendation || recommendationClicks.has(event)) return
  try {
    const items = commerceItems([{ ...item, qty: 1 }])
    if (!items.length) return
    recommendationClicks.add(event)
    trackCommerce('recommendation_click', { items, recommendation })
  } catch { /* Product navigation must work when analytics is unavailable. */ }
}

let lastVisitDay = ''
const visitDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: COMMERCE_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
})
let eventSequence = 0
let documentId = ''
let lastSearchQuery = ''
let submittedSearch: SearchCapture | undefined
let routeSearchQuery = ''
let routeSearchCapture: SearchCapture | undefined

export const resetCompletedSearch = (): void => {
  lastSearchQuery = ''
  submittedSearch = undefined
  routeSearchQuery = ''
  routeSearchCapture = undefined
}

const validSearchCount = (count: number) => Number.isSafeInteger(count) && count >= 0 && count <= 1_000_000

export type SearchCapture = {
  readonly query: string
  readonly completed: boolean
  complete: (resultCount: number) => void
}

const createSearchCapture = (query: string, completed = false): SearchCapture => ({
  query,
  get completed() { return completed },
  complete: resultCount => {
    if (completed || !validSearchCount(resultCount)) return
    completed = true
    lastSearchQuery = query
    trackCommerce('search', { query, resultCount })
  },
})

/** One overlay interaction shares captures across idle, clicks and submission.
 * Captures survive navigation independently of route-level deduplication. */
export const createSearchTrackingCycle = () => {
  const captures = new Map<string, SearchCapture>()
  return (rawQuery: string): SearchCapture | undefined => {
    const query = normalizeCommerceSearchQuery(rawQuery)
    if (!query) return undefined
    const existing = captures.get(query)
    if (existing) return existing
    const capture = createSearchCapture(query)
    captures.set(query, capture)
    return capture
  }
}

/** Share the header's capture with the destination's complete listing response. */
export const submitSearchCapture = (capture: SearchCapture | undefined): void => { submittedSearch = capture }

/** Only catalog identifiers and quantities leave the cart, never its full data. */
export const commerceItems = (items: ReadonlyArray<{ id: number; key?: string; qty: number }>): CommerceItem[] => items
  .filter(item => Number.isSafeInteger(item.id) && item.id > 0 && Number.isSafeInteger(item.qty) && item.qty > 0)
  .slice(0, 100)
  .map(item => ({
    id: item.id,
    ...(typeof item.key === 'string' && item.key.length <= 120 ? { key: item.key } : {}),
    qty: Math.min(item.qty, 999),
  }))

/** Analytics must never interrupt shopping, including when beacon/fetch throws. */
export const trackCommerce = (event: CommerceEvent, details: CommerceDetails = {}): void => {
  if (typeof window === 'undefined') return
  try {
    const visitDay = event === 'visit' ? visitDayFormatter.format(new Date()) : ''
    if (event === 'visit' && visitDay === lastVisitDay) return
    if (!documentId) documentId = window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
    const payload = {
      host: window.location.hostname,
      event,
      ...(['add_to_cart', 'begin_checkout'].includes(event) ? { cartId: getCartIdentity() } : {}),
      eventId: `${documentId}-${++eventSequence}`,
      ...details,
    }
    const body = JSON.stringify(payload)
    const endpoint = `${config.api_server.online}/tracking/commerce`
    if (event === 'visit') lastVisitDay = visitDay
    try {
      if (window.navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'application/json' }))) return
    } catch { /* Some browsers reject beacons; use the same event ID for fallback. */ }
    void fetch(endpoint, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      keepalive: true,
    }).catch(() => undefined)
  } catch { /* Tracking is optional for the shopping flow. */ }
}

/** Complete global searches only; page/facet changes and remounts do not add searches. */
export const trackCompletedSearch = (rawQuery: string, resultCount: number): void => {
  const query = normalizeCommerceSearchQuery(rawQuery)
  if (!query || !validSearchCount(resultCount)) return
  if (submittedSearch?.query === query) {
    routeSearchCapture = submittedSearch
    submittedSearch = undefined
  } else if (routeSearchQuery !== query) {
    routeSearchCapture = undefined
  }
  routeSearchQuery = query
  // Keep route dedup independent of a different, late committed click response.
  routeSearchCapture ||= createSearchCapture(query, query === lastSearchQuery)
  routeSearchCapture.complete(resultCount)
}
